/**
 * The single door to the API.
 *
 * Centralised here so no screen re-implements any of it (plan §6):
 *
 *  1. `credentials: 'include'` on every request — the session is an HttpOnly
 *     cookie on api.fiscalfounders.com. That is cross-ORIGIN but same-SITE, so
 *     the cookie is sent; CORS is what makes the response readable.
 *  2. `x-ff-csrf` on every mutation, double-submitted from the token the API
 *     returned. Held in memory only — see the note on `csrfToken`.
 *  3. The error envelope unwrapped into a typed `ApiError`, so a screen branches
 *     on `err.code === 'QBO_NEEDS_REAUTH'` rather than matching strings.
 *  4. A global 401 hook that clears session state, while leaving expected domain
 *     errors (`QBO_NOT_CONNECTED`, `QBO_COMPANY_NAME_MISMATCH`) to the caller.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

export const FF_ENV = import.meta.env.VITE_FF_ENV as 'dev' | 'prod';

/** Mirrors LLD §13.3. Kept as a union so a typo in a branch is a type error. */
export type ApiErrorCode =
  | 'VALIDATION_FAILED'
  | 'OAUTH_STATE_INVALID'
  | 'QBO_AUTHORIZATION_FAILED'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'QBO_FORBIDDEN'
  | 'NOT_FOUND'
  | 'QBO_REALM_ALREADY_BOUND'
  | 'QBO_ALREADY_CONNECTED'
  | 'QBO_COMPANY_NAME_MISMATCH'
  | 'QBO_NOT_CONNECTED'
  | 'QBO_RATE_LIMITED'
  | 'QBO_UPSTREAM_ERROR'
  | 'QBO_NEEDS_REAUTH'
  | 'QBO_UPSTREAM_TIMEOUT'
  | 'CONFIG_INVALID'
  | 'INTERNAL_ERROR'
  | 'NETWORK';

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly status: number,
    /** Quote this when reporting a problem — it ties to the API's logs. */
    readonly requestId: string | null,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The CSRF token lives in a module variable, never `localStorage`.
 *
 * `localStorage` survives the tab, so on a shared machine the next person
 * inherits it, and any XSS reads it synchronously. Memory dies with the tab, and
 * `GET /v1/me` restores it on reload — which is the correct source of truth
 * anyway, because it also proves the cookie is still valid.
 */
let csrfToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Registered once by App so a 401 anywhere returns the user to /login. */
export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Set on the login call, which legitimately 401s and must not trigger the hook. */
  suppressUnauthenticatedHandler?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { ...opts.headers };

  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  // GETs are exempt by design; sending it anyway would be harmless but implies
  // the token is needed to read, which it is not.
  if (method !== 'GET' && csrfToken) headers['x-ff-csrf'] = csrfToken;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch (err) {
    // A failed fetch is indistinguishable from offline, DNS failure and a CORS
    // rejection. Say so plainly rather than inventing a cause.
    throw new ApiError(
      'NETWORK',
      'Could not reach the API. Check your connection and try again.',
      0,
      null,
      { cause: String(err) },
    );
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    const envelope = parsed as { error?: { code?: string; message?: string; requestId?: string; details?: Record<string, unknown> } } | null;
    const code = (envelope?.error?.code ?? 'INTERNAL_ERROR') as ApiErrorCode;
    const err = new ApiError(
      code,
      envelope?.error?.message ?? `Request failed (${res.status})`,
      res.status,
      envelope?.error?.requestId ?? null,
      envelope?.error?.details ?? {},
    );
    if (code === 'UNAUTHENTICATED' && !opts.suppressUnauthenticatedHandler) {
      csrfToken = null;
      onUnauthenticated?.();
    }
    throw err;
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Response shapes — only the fields the UI reads.
// ---------------------------------------------------------------------------

export type ConnectionStatus = 'pending' | 'connected' | 'needs_reauth' | 'revoked' | 'error';

export interface Me {
  user: { id: string; email: string; name: string; avatarUrl: string | null };
  activeFirm: { id: string; name: string; slug: string; role: string } | null;
  memberships: { firmId: string; firmName: string; role: string }[];
  capabilities: string[];
  csrfToken: string;
  authMode: 'dev' | 'google';
}

export interface ClientSummary {
  id: string;
  firmId: string;
  name: string;
  legalName: string | null;
  status: string;
  capabilities: string[];
  quickbooks: {
    connectionId: string | null;
    status: ConnectionStatus;
    companyName: string | null;
    lastSuccessAt: string | null;
    consecutiveFailures: number | null;
    lastErrorCode: string | null;
  } | null;
}

export interface ConnectionDetail {
  connectionId: string;
  provider: string;
  environment: string;
  status: ConnectionStatus;
  realmId: string | null;
  companyName: string | null;
  legalName: string | null;
  country: string | null;
  fiscalYearStartMonth: string | null;
  scopes: string[];
  connectedAt: string | null;
  connectedBy: { id: string; name: string } | null;
  lastSuccessAt: string | null;
  lastError: { code: string; at: string; consecutiveFailures: number } | null;
  accessTokenExpiresAt: string | null;
  refreshTokenExpiresAt: string | null;
}

export interface ClientDetail {
  id: string;
  firmId: string;
  name: string;
  legalName: string | null;
  status: string;
  createdAt: string;
  capabilities: string[];
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export const api = {
  async login(email: string, secret: string): Promise<Me> {
    /**
     * Written as a POSITIVE branch on the build-time constant, deliberately.
     *
     * Vite substitutes `import.meta.env.VITE_FF_ENV` with a literal, so in a prod
     * build this reads `if ('prod' === 'dev')` and esbuild removes the whole
     * block — including the dev header names. An early `throw` followed by the
     * dev call would leave those strings in the bundle, because eliminating code
     * after an unconditional throw is not something a minifier reliably does.
     *
     * The prod bundle is asserted to contain neither header name.
     */
    if (import.meta.env.VITE_FF_ENV === 'dev') {
      // The secret is sent as a header and never persisted; when Google OIDC
      // lands this becomes a redirect and nothing else here changes.
      const res = await request<{ csrfToken: string }>('/v1/session', {
        method: 'POST',
        headers: { 'x-ff-dev-user': email, 'x-ff-dev-secret': secret },
        body: {},
        suppressUnauthenticatedHandler: true,
      });
      setCsrfToken(res.csrfToken);
      return api.me();
    }
    throw new ApiError(
      'CONFIG_INVALID',
      'Sign-in is not available in this build.',
      0,
      null,
    );
  },

  async me(): Promise<Me> {
    const me = await request<Me>('/v1/me', { suppressUnauthenticatedHandler: true });
    setCsrfToken(me.csrfToken);
    return me;
  },

  async logout(): Promise<void> {
    await request<void>('/v1/session', { method: 'DELETE' });
    setCsrfToken(null);
  },

  clients: () => request<{ clients: ClientSummary[] }>('/v1/clients'),

  client: (id: string) => request<ClientDetail>(`/v1/clients/${id}`),

  connection: (id: string) =>
    request<ConnectionDetail>(`/v1/clients/${id}/integrations/quickbooks`),

  connect: (id: string, redirectAfter: string) =>
    request<{ authorizationUrl: string; expiresAt: string }>(
      `/v1/clients/${id}/integrations/quickbooks/connect`,
      { method: 'POST', body: { redirectAfter } },
    ),

  confirm: (id: string, connectionId: string) =>
    request<{ connectionId: string; status: string; companyName: string | null }>(
      `/v1/clients/${id}/integrations/quickbooks/confirm`,
      { method: 'POST', body: { connectionId } },
    ),

  disconnect: (id: string) =>
    request<{ status: string; revokedUpstream: boolean }>(
      `/v1/clients/${id}/integrations/quickbooks`,
      { method: 'DELETE' },
    ),

  qboCompany: (id: string) => request<Record<string, unknown>>(`/v1/clients/${id}/qbo/company`),

  qboAccounts: (id: string) =>
    request<{ accounts: Record<string, unknown>[]; count: number; truncated: boolean }>(
      `/v1/clients/${id}/qbo/accounts`,
    ),
};
